var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { ComponentRef, ErrorHandler, Injector, NgModuleFactory, NgModuleFactoryLoader, Optional, Provider, SkipSelf, ViewContainerRef, } from '@angular/core';
import { ROUTES } from '@angular/router';
import { retryBackoff } from 'backoff-rxjs';
import * as Rx from 'rxjs';
import { catchError, retryWhen, switchMap, tap } from 'rxjs/operators';
/**
 * Custom injector to be used when providing custom
 * injection tokens to components inside a portal.
 */
var PortalInjector = /** @class */ (function () {
    function PortalInjector(_parentInjector, _customTokens) {
        this._parentInjector = _parentInjector;
        this._customTokens = _customTokens;
    }
    PortalInjector.prototype.get = function (token, notFoundValue) {
        var value = this._customTokens.get(token);
        if (typeof value !== 'undefined') {
            return value;
        }
        return this._parentInjector.get(token, notFoundValue);
    };
    return PortalInjector;
}());
export { PortalInjector };
var ModalRef = /** @class */ (function () {
    function ModalRef(_openStreamSubject$) {
        this._openStreamSubject$ = _openStreamSubject$;
        /** Subject that emits when the modal finishes opening. */
        this._afterOpenSubject$ = new Rx.Subject();
        /** Subject that emits after the modal finishes closing and returns a result */
        this._afterClosedSubject$ = new Rx.Subject();
    }
    ModalRef.prototype.set = function (componentRef) {
        this.componentRef = componentRef;
        this._afterOpenSubject$.next();
        this._afterOpenSubject$.complete();
    };
    ModalRef.prototype.close = function (result) {
        // if the modal failed to load (e.g. network error), we might not have a
        // componentRef
        if (this.componentRef) {
            this.componentRef.destroy();
        }
        this._openStreamSubject$.next(false);
        this._afterClosedSubject$.next(result);
        this._afterClosedSubject$.complete();
    };
    ModalRef.prototype.afterOpen = function () {
        return this._afterOpenSubject$.asObservable();
    };
    ModalRef.prototype.afterClosed = function () {
        return this._afterClosedSubject$.asObservable();
    };
    return ModalRef;
}());
export { ModalRef };
var ModalService = /** @class */ (function () {
    function ModalService(
    // tslint:disable-next-line:deprecation FIXME: T106089
    loader, loadableRoutes, errorHandler) {
        this.loader = loader;
        this.loadableRoutes = loadableRoutes;
        this.errorHandler = errorHandler;
        this._openStreamSubject$ = new Rx.Subject();
        this.openStream$ = this._openStreamSubject$.asObservable();
        this._statusStreamSubject$ = new Rx.Subject();
        this.statusStream$ = this._statusStreamSubject$.asObservable();
    }
    ModalService.prototype.close = function () {
        if (this.openModalRef) {
            this.openModalRef.close();
            this.openModalRef = undefined;
        }
        if (this.retryStreamSubject$) {
            this.retryStreamSubject$.complete();
        }
    };
    ModalService.prototype.open = function (component, modalConfig) {
        var _this = this;
        var ngModule = this.getModalNgModule(component);
        this.modalConfig = modalConfig;
        this._openStreamSubject$.next(modalConfig.showBackdrop !== undefined ? modalConfig.showBackdrop : true);
        var modalRef = new ModalRef(this._openStreamSubject$);
        this.openModalRef = modalRef;
        this.retryStreamSubject$ = new Rx.Subject();
        // asynchronously set the componentRef in the modal
        // we return immediately to make the API nicer.
        Rx.of(true)
            .pipe(
        // reset the loading status
        tap(function () { return _this._statusStreamSubject$.next({ ready: false }); }), switchMap(function () { return _this.loader.load(ngModule); }), 
        // on failure retry twice with exponential backoff
        retryBackoff({
            initialInterval: 200,
            maxRetries: 2,
        }), catchError(function (err) {
            // report the error to Sentry since it failed 3 times
            _this.errorHandler.handleError(err);
            // inform the modal component that the modal couldn't be loaded
            _this._statusStreamSubject$.next({
                ready: true,
                errorMessage: err.message,
            });
            throw err;
        }), 
        // this subject allows the user to manual retry on errors
        retryWhen(function () { return _this.retryStreamSubject$.asObservable(); }))
            .subscribe(function (ngModuleFactory) {
            // inform modal component that loading succeeded
            _this._statusStreamSubject$.next({ ready: true });
            // get component factory
            var componentFactory = ngModuleFactory
                .create(_this.injector) // ngModuleRef
                .componentFactoryResolver.resolveComponentFactory(component);
            // create custom injector
            var injectionTokens = new WeakMap();
            injectionTokens.set(ModalRef, modalRef);
            var customInjector = new PortalInjector(_this.injector, injectionTokens);
            // create component
            var componentRef = _this.viewContainerRef.createComponent(componentFactory, 0, customInjector);
            if (modalConfig.inputs) {
                Object.entries(modalConfig.inputs).forEach(function (_a) {
                    var key = _a[0], value = _a[1];
                    // FIXME: ComponentType<T> has no index signature.
                    componentRef.instance[key] = value;
                });
            }
            modalRef.set(componentRef);
        });
        return modalRef;
    };
    ModalService.prototype.retryOpen = function () {
        this.retryStreamSubject$.next(undefined);
    };
    ModalService.prototype.setContainer = function (viewContainerRef, injector) {
        this.viewContainerRef = viewContainerRef;
        this.injector = injector;
    };
    ModalService.prototype.getCurrentConfig = function () {
        return this.modalConfig;
    };
    ModalService.prototype.getModalNgModule = function (component) {
        var componentRoute = this.loadableRoutes
            .reduce(function (acc, list) { return __spreadArrays(acc, list); }, []) // flatten
            .find(function (route) { return route.component === component; }); // find component
        if (componentRoute && typeof componentRoute.loadChildren === 'string') {
            return componentRoute.loadChildren;
        }
        throw new Error("missing modal module configuration for " + component.name);
    };
    return ModalService;
}());
export { ModalService };
/**
 * Provider factory for Modal Service
 *
 * @export
 * @param {ModalService} parentRegistry The parent registry
 * @returns {ModalService} The singleton instance or a new one
 */
export function MODAL_SERVICE_PROVIDER_FACTORY(parentRegistry, 
// tslint:disable-next-line:deprecation FIXME: T106089
loader, loadableRoutes, errorHandler) {
    return (parentRegistry || new ModalService(loader, loadableRoutes, errorHandler));
}
/**
 * Create a service provider that acts as a singleton.
 */
export var MODAL_SERVICE_PROVIDER = {
    // If there is already an ModalService available, use that.
    // Otherwise, provide a new one.
    provide: ModalService,
    deps: [
        [new Optional(), new SkipSelf(), ModalService],
        // tslint:disable-next-line:deprecation FIXME: T106089
        NgModuleFactoryLoader,
        ROUTES,
        ErrorHandler,
    ],
    useFactory: MODAL_SERVICE_PROVIDER_FACTORY,
};
